/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FC, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { Cart, LoadingSpinner } from '@components';
import { DeliveryDetails } from './DeliveryDetails/DeliveryDetails';
import ConfirmOrder from './ConfirmOrder/ConfirmOrder';
import { OrderProductList } from './OrderProductList/OrderProductList';
import PaymentMethod from './PaymentMethods/PaymentMethod';
import {
  useGetDeliveryFeeQuery,
  useGetSuburbHoursQuery,
  useSendGettingPaymentMethodQuery,
  useGetSuburbDeliveryETAQuery,
  useCheckBlackListStatusQuery,
} from '@services';
import { useAppSelector } from '@hooks/redux-custom';
import TimePanel from './TimePanel/TimePanel';
import { AU_STATES } from '@constants';
import { DeliveryType } from '@models';

const OrdersPage: FC = () => {
  const [deliveryFeeChecked, setDeliveryFeeChecked] = useState<boolean>(true);
  const history = useHistory();
  const customer = useAppSelector((state) => state.customer.value.selectedCustomer);
  const { data } = useCheckBlackListStatusQuery(customer?.phone);
  const isBlacklisted = data?.isBlacklisted;
  const { shippingAddress } = useAppSelector((state) => state.order.value);

  if (!shippingAddress) history.push('/customers');

  /** Capture suburb and postcode from shipping address */
  const suburbString = useMemo(
    () => (shippingAddress ? `${shippingAddress.postal_code}_${shippingAddress.city.toLowerCase().replace(/\s/g, '')}` : ''),
    [shippingAddress],
  );

  const selectedState = AU_STATES.find((state) => state.value === shippingAddress?.state_or_province)?.id ?? '';

  const { data: deliveryFeeData } = useGetDeliveryFeeQuery({ state: selectedState });

  const deliveryFee = deliveryFeeData && deliveryFeeData.find((rate) => rate.name === DeliveryType.FlatRate)?.settings.rate;

  const { data: avaTradingTimes, isFetching } = useGetSuburbHoursQuery({ suburb: suburbString });

  const { data: suburbDeliveryETA } = useGetSuburbDeliveryETAQuery({
    postcode: suburbString.split('_')[0],
  });

  const paymentMethods = useSendGettingPaymentMethodQuery(customer?.id ?? '');

  /** show loading screen until fetch */
  if (isFetching) return <LoadingSpinner isOpen={isFetching} />;

  return (
    <Grid container spacing={1} data-testid="orders-page">
      <Grid item flex={1}>
        <OrderProductList />
      </Grid>
      <Grid item sm={4}>
        {window.FEATURE_FLAGS.AVA_INTEGRATION_23 && (
          <TimePanel tradingTimes={avaTradingTimes!} eta={suburbDeliveryETA} suburb={suburbString.split('_')[1]} />
        )}
        <Cart deliveryFee={deliveryFee} setDeliveryFee={setDeliveryFeeChecked} />
        <PaymentMethod methods={paymentMethods?.data} />
        <DeliveryDetails />
        <ConfirmOrder
          avaTradingTimes={avaTradingTimes!}
          isDeliveryFeeIncluded={deliveryFeeChecked}
          isBlacklisted={isBlacklisted}
          isBlackListedData={data}
        />
      </Grid>
    </Grid>
  );
};

export default OrdersPage;
